import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { BodySecondary, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useEffect } from 'react';

import { ExcludedHousesTable } from '@/components/Modals/ExcludeHouses/ExcludedHousesTable';
import { useExcludeHouses } from '@/components/Modals/ExcludeHouses/hooks/useExcludeHouses';
import { HousesToExcludeTable } from '@/components/Modals/ExcludeHouses/HousesToExcludeTable';

type ApplyReadyParams = { handleApply: () => Promise<void>; hasChanges: boolean };

type Props = {
    onApplyReady?: (params: ApplyReadyParams | null) => void;
};

export const PreferencesFiltersExcludeHouses = ({ onApplyReady }: Props) => {
    const {
        handleApplyExcludeHouses,
        handleRemoveHouse,
        handleSelectHouse,
        hasExcludedHousesChanges,
        hasHousesToExcludeChanges,
        housesToExcludeData,
        isLoadingHouses,
        localExcludedHouses,
    } = useExcludeHouses();

    useEffect(() => {
        onApplyReady?.({
            handleApply: handleApplyExcludeHouses,
            hasChanges: hasExcludedHousesChanges || hasHousesToExcludeChanges,
        });
        return () => onApplyReady?.(null);
    }, [onApplyReady, handleApplyExcludeHouses, hasExcludedHousesChanges, hasHousesToExcludeChanges]);

    const renderHousesToExclude = () => {
        if (isLoadingHouses) {
            return (
                <div className="flex justify-center py-md">
                    <Loading />
                </div>
            );
        }

        if (housesToExcludeData.length === 0) {
            return (
                <H5>
                    <FormattedMessage id="excludeAuctionHousesFilter.modal.noHousesToExclude" />
                </H5>
            );
        }

        return (
            <HousesToExcludeTable
                houseData={housesToExcludeData}
                onSelectHouse={handleSelectHouse}
            />
        );
    };

    return (
        <div className="flex flex-col">
            <ExcludedHousesTable
                className={cn(hasExcludedHousesChanges ? 'mb-2xs' : 'mb-md')}
                houseData={localExcludedHouses}
                onRemoveHouse={handleRemoveHouse}
            />
            {hasExcludedHousesChanges && (
                <BodySecondary className="mb-md flex text-text-secondary">
                    <FormattedMessage id="excludeAuctionHousesFilter.modal.nonAppliedChanges" />
                </BodySecondary>
            )}
            {renderHousesToExclude()}
        </div>
    );
};
